// Sidebar.jsx
import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { RxDashboard } from "react-icons/rx";
import { MdOutlineCategory } from "react-icons/md";
import { RiCoupon3Line } from "react-icons/ri";
import { FiLogOut, FiMoon, FiSun, FiCopy } from "react-icons/fi";
import { FaChevronDown, FaCog, FaUserShield } from "react-icons/fa";
import { AiOutlineMenuFold, AiOutlineMenuUnfold } from "react-icons/ai";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { GrAction } from "react-icons/gr";
import { IoGitMergeOutline } from "react-icons/io5";
import { GrUserManager } from "react-icons/gr";

const Sidebar = ({ isCollapsed, setIsCollapsed, darkMode, setDarkMode }) => {
  const [openMenu, setOpenMenu] = React.useState("");
  const permissions = useSelector((state) => state.auth?.permissions || []);
  const navigate = useNavigate();

  const hasPermission = (perm) => permissions.includes(perm);

  const linkClass = (isActive) =>
    classNames(
      "flex items-center font-medium rounded-xl transition-all duration-200 group",
      {
        "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25":
          isActive,
        "text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800":
          !isActive,
      },
      isCollapsed ? "justify-center p-3" : "gap-3 py-3 px-3",
    );

  const toggleMenu = (menu) => {
    setOpenMenu((prev) => (prev === menu ? "" : menu));
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <div
      className={classNames(
        "bg-white dark:bg-gray-900 h-screen sticky top-0 flex flex-col shadow-xl transition-all duration-300 overflow-hidden border-r border-gray-200 dark:border-gray-700",
        { "w-80": !isCollapsed, "w-20": isCollapsed },
      )}
    >
      {/* Header */}
      <div
        className={classNames(
          "flex items-center",
          isCollapsed ? "p-4 flex-col gap-4" : "p-6 justify-between",
        )}
      >
        {!isCollapsed ? (
          <>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">E</span>
              </div>
              <div>
                <span className="font-bold text-xl text-gray-900 dark:text-white block">
                  Ebluesys
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Admin Panel
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title={darkMode ? "Light Mode" : "Dark Mode"}
              >
                {darkMode ? (
                  <FiMoon className="text-lg" />
                ) : (
                  <FiSun className="text-lg" />
                )}
              </button>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title="Collapse"
              >
                <AiOutlineMenuFold className="text-lg" />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">E</span>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title={darkMode ? "Light Mode" : "Dark Mode"}
              >
                {/* {darkMode ? <FiSun className="text-lg" /> : <FiMoon className="text-lg" />} */}
                {darkMode ? (
                  <FiMoon className="text-lg" />
                ) : (
                  <FiSun className="text-lg" />
                )}
              </button>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title="Expand"
              >
                <AiOutlineMenuUnfold className="text-lg" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav
        className={classNames(
          "flex flex-col gap-2 flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 dark:hover:scrollbar-thumb-gray-500 scrollbar-track-transparent",
          isCollapsed ? "px-2" : "px-3",
        )}
      >
        {hasPermission("view_dashboard") && (
          <NavLink
            to="/dashboard"
            className={({ isActive }) => linkClass(isActive)}
            title="Dashboard"
          >
            <div
              className={classNames(
                "rounded-lg transition",
                isCollapsed
                  ? "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-blue-500/10"
                  : "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-blue-500/10",
              )}
            >
              <RxDashboard
                className={classNames(
                  "text-gray-600 dark:text-gray-400 group-hover:text-blue-500 transition",
                  isCollapsed ? "text-xl" : "text-xl",
                )}
              />
            </div>
            {!isCollapsed && <span className="font-medium">Dashboard</span>}
          </NavLink>
        )}

        {(hasPermission("manage_users") ||
          hasPermission("view_attendance")) && (
          <SidebarGroup
            title="User Management"
            icon={<GrUserManager />}
            menuKey="user"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
            {hasPermission("manage_users") && (
              <NavLink
                to="/dashboard/employee"
                className={({ isActive }) => linkClass(isActive)}
                title="Employee"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Employee
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("view_attendance") && (
              <NavLink
                to="/dashboard/attendance"
                className={({ isActive }) => linkClass(isActive)}
                title="Attendance"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Attendance
                  </>
                )}
              </NavLink>
            )}
             {hasPermission("direct_attendance_entry") && (
              <NavLink
                to="/dashboard/direct-Attendance"
                className={({ isActive }) => linkClass(isActive)}
                title="directAttendance"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Direct Attendance
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("view_attendance_report") && (
              <NavLink
                to="/dashboard/attendance-report"
                className={({ isActive }) => linkClass(isActive)}
                title="Attendance Report"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Attendance Report
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("User_add") && (
              <NavLink
                to="/dashboard/User-add"
                className={({ isActive }) => linkClass(isActive)}
                title="User Add"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    User Add
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("User_Add_Bulk") && (
              <NavLink
                to="/dashboard/User-add-bulk"
                className={({ isActive }) => linkClass(isActive)}
                title="Multiple User Add"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Multiple User Add
                  </>
                )}
              </NavLink>
            )}
          </SidebarGroup>
        )}

        {hasPermission("view_tasks") && (
          <SidebarGroup
            title="Task Management"
            icon={<RiCoupon3Line />}
            menuKey="task"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
            {hasPermission("manage_tasks") && (
              <NavLink
                to="/dashboard/task"
                className={({ isActive }) => linkClass(isActive)}
                title="Task Details"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Task Details
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("view_tasks_track") && (
              <NavLink
                to="/dashboard/task-tracking"
                className={({ isActive }) => linkClass(isActive)}
                title="Task Tracking"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Task Tracking
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("Create_tasks_admin") && (
              <NavLink
                to="/dashboard/Create-Tasks-Admin"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Tasks"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Tasks
                  </>
                )}
              </NavLink>
            )}
          </SidebarGroup>
        )}

        {hasPermission("view_leaves") && (
          <SidebarGroup
            title="Leaves Management"
            icon={<GrAction />}
            menuKey="leave"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
            {hasPermission("view_leaves") && (
              <NavLink
                to="/dashboard/leaves"
                className={({ isActive }) => linkClass(isActive)}
                title="Leave Details"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Leave Details
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("view_leave_types") && (
              <NavLink
                to="/dashboard/view-leave-types"
                className={({ isActive }) => linkClass(isActive)}
                title="Leave Types"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Leave Types
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("create_leave_types") && (
              <NavLink
                to="/dashboard/create-leave-type"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Leave Type"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Leave Type
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("manage_users") && (
              <NavLink
                to="/dashboard/Employees-Remaining-Leaves"
                className={({ isActive }) => linkClass(isActive)}
                title="Remaining Leaves"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Remaining Leaves
                  </>
                )}
              </NavLink>
            )}
          </SidebarGroup>
        )}

        {hasPermission("districts_view") && (
          <SidebarGroup
            title="ULB Management"
            icon={<FiCopy />}
            menuKey="ulb"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
            {hasPermission("districts_view") && (
              <NavLink
                to="/dashboard/View-districts"
                className={({ isActive }) => linkClass(isActive)}
                title="District Details"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    District Details
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("districts_create") && (
              <NavLink
                to="/dashboard/Create-Districts"
                className={({ isActive }) => linkClass(isActive)}
                title="Create District"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create District
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("municipalities_view") && (
              <NavLink
                to="/dashboard/View-Municipalitie"
                className={({ isActive }) => linkClass(isActive)}
                title="Municipalities"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Municipalities
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("municipalities_create") && (
              <NavLink
                to="/dashboard/Create-Municipality"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Municipalities"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Municipalities
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("municipalities_admin_view") && (
              <NavLink
                to="/dashboard/View-Admin"
                className={({ isActive }) => linkClass(isActive)}
                title="ULB Admin"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    ULB Admin
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("municipalities_admin_create") && (
              <NavLink
                to="/dashboard/Create-Admin"
                className={({ isActive }) => linkClass(isActive)}
                title="Create ULB Admin"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create ULB Admin
                  </>
                )}
              </NavLink>
            )}
          </SidebarGroup>
        )}

        {(hasPermission("view_tasks_title") ||
          hasPermission("create_tasks_title") ||
          hasPermission("view_holiday") ||
          hasPermission("create_holiday")) && (
          <SidebarGroup
            title="Settings"
            icon={<FaCog />}
            menuKey="setting"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
            {hasPermission("view_tasks_title") && (
              <NavLink
                to="/dashboard/task-title"
                className={({ isActive }) => linkClass(isActive)}
                title="Task Titles"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Task Titles
                  </>
                )}
              </NavLink>
            )}
            {/* {hasPermission("create_tasks_title") && (
              <NavLink
                to="/dashboard/create-task-title"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Task Title"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Task Title
                  </>
                )}
              </NavLink>
            )} */}
            {hasPermission("view_holiday") && (
              <NavLink
                to="/dashboard/view-holiday"
                className={({ isActive }) => linkClass(isActive)}
                title="Holidays"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Holidays
                  </>
                )}
              </NavLink>
            )}
            {/* {hasPermission("create_holiday") && (
              <NavLink
                to="/dashboard/create-holiday"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Holiday"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Holiday
                  </>
                )}
              </NavLink>
            )} */}
            {hasPermission("view_designation") && (
              <NavLink
                to="/dashboard/view-Designations"
                className={({ isActive }) => linkClass(isActive)}
                title="View Designations"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Designations
                  </>
                )}
              </NavLink>
            )}

            {/* {hasPermission("create_designation") && (
              <NavLink
                to="/dashboard/create-Designation"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Designation"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Designation
                  </>
                )}
              </NavLink>
            )} */}

            {hasPermission("view_office") && (
              <NavLink
                to="/dashboard/view-offices"
                className={({ isActive }) => linkClass(isActive)}
                title="View Offices"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Offices
                  </>
                )}
              </NavLink>
            )}
            {/* 
            {hasPermission("create_office") && (
              <NavLink
                to="/dashboard/create-office"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Office"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Office
                  </>
                )}
              </NavLink>
            )} */}

            {/*shifts section */}

            {hasPermission("view_shift") && (
              <NavLink
                to="/dashboard/view-shifts"
                className={({ isActive }) => linkClass(isActive)}
                title="View Shift"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Shift
                  </>
                )}
              </NavLink>
            )}

            {/* {hasPermission("create_shift") && (
              <NavLink
                to="/dashboard/create-shift"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Shift"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Shift
                  </>
                )}
              </NavLink>
            )} */}

            {hasPermission("view_locations_Dropdown") && (
              <NavLink
                to="/dashboard/visit-Location"
                className={({ isActive }) => linkClass(isActive)}
                title="Visit Location"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Visit Location
                  </>
                )}
              </NavLink>
            )}
            {/* {hasPermission("create_locations_Dropdown") && (
              <NavLink
                to="/dashboard/Create-Visit-Location"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Visit Location"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Visit Location
                  </>
                )}
              </NavLink>
            )} */}
          </SidebarGroup>
        )}

        {hasPermission("web_admin") && (
          <SidebarGroup
            title="Web Admin"
            icon={<GrUserManager />}
            menuKey="web-admin"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
                        {hasPermission("view_job_post") && (
              <NavLink
                to="/dashboard/jobs"
                className={({ isActive }) => linkClass(isActive)}
                title="Jobs"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Jobs
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("view_blog") && (
              <NavLink
                to="/dashboard/blogs"
                className={({ isActive }) => linkClass(isActive)}
                title="Blogs"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Blogs
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("Contact_Details") && (
              <NavLink
                to="/dashboard/Contact-Details"
                className={({ isActive }) => linkClass(isActive)}
                title="Contact Details"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Contact Details
                  </>
                )}
              </NavLink>
            )}

            {hasPermission("Resume_Details") && (
              <NavLink
                to="/dashboard/Resume-Details"
                className={({ isActive }) => linkClass(isActive)}
                title="Resume Details"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Resume Details
                  </>
                )}
              </NavLink>
            )}
          </SidebarGroup>
        )}

        {(hasPermission("create_role") ||
          hasPermission("create_permission") ||
          hasPermission("assign_permission_to_role")) && (
          <SidebarGroup
            title="Role Management"
            icon={<FaUserShield />}
            menuKey="role"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
            {hasPermission("create_role") && (
              <NavLink
                to="/dashboard/create-role"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Role"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Role
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("create_permission") && (
              <NavLink
                to="/dashboard/create-permission"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Permission"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Permission
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("assign_permission_to_role") && (
              <NavLink
                to="/dashboard/assign-permission"
                className={({ isActive }) => linkClass(isActive)}
                title="Assign Permission"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Assign Permission
                  </>
                )}
              </NavLink>
            )}
          </SidebarGroup>
        )}

        {hasPermission("view_geofence") && (
          <>
            <NavLink
              to="/dashboard/geofence"
              className={({ isActive }) => linkClass(isActive)}
              title="Geofence"
            >
              <div
                className={classNames(
                  "rounded-lg transition",
                  isCollapsed
                    ? "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-blue-500/10"
                    : "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-blue-500/10",
                )}
              >
                <IoGitMergeOutline
                  className={classNames(
                    "text-gray-600 dark:text-gray-400 group-hover:text-blue-500 transition",
                    isCollapsed ? "text-xl" : "text-xl",
                  )}
                />
              </div>
              {!isCollapsed && "Geofence"}
            </NavLink>
            <NavLink
              to="/dashboard/parkGeofence"
              className={({ isActive }) => linkClass(isActive)}
              title="Park Geofence"
            >
              <div
                className={classNames(
                  "rounded-lg transition",
                  isCollapsed
                    ? "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-blue-500/10"
                    : "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-blue-500/10",
                )}
              >
                <IoGitMergeOutline
                  className={classNames(
                    "text-gray-600 dark:text-gray-400 group-hover:text-blue-500 transition",
                    isCollapsed ? "text-xl" : "text-xl",
                  )}
                />
              </div>
              {!isCollapsed && "Park Geofence"}
            </NavLink>
          </>
        )}
      </nav>

      {/* Logout Button */}
      <div className={classNames(isCollapsed ? "p-2" : "p-3")}>
        <button
          onClick={handleLogout}
          className={classNames(
            "flex items-center font-medium rounded-xl text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 group w-full",
            isCollapsed ? "justify-center p-3" : "gap-3 py-3 px-3",
          )}
          title="Logout"
        >
          <div
            className={classNames(
              "rounded-lg transition",
              isCollapsed
                ? "p-2 bg-red-100 dark:bg-red-900/30 group-hover:bg-red-200 dark:group-hover:bg-red-900/50"
                : "p-2 bg-red-100 dark:bg-red-900/30 group-hover:bg-red-200 dark:group-hover:bg-red-900/50",
            )}
          >
            <FiLogOut
              className={classNames(isCollapsed ? "text-xl" : "text-xl")}
            />
          </div>
          {!isCollapsed && "Logout"}
        </button>
      </div>
    </div>
  );
};

const SidebarGroup = ({
  title,
  icon,
  menuKey,
  isCollapsed,
  openMenu,
  toggleMenu,
  children,
}) => {
  const groupButtonClass = classNames(
    "flex items-center w-full rounded-xl transition-all duration-200 group",
    {
      "bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white":
        openMenu === menuKey,
      "text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800":
        openMenu !== menuKey,
    },
    isCollapsed ? "justify-center p-3" : "justify-between py-3 px-3",
  );

  return (
    <div>
      <button
        onClick={() => toggleMenu(menuKey)}
        className={groupButtonClass}
        title={isCollapsed ? title : undefined}
      >
        <div className="flex items-center gap-3">
          <div
            className={classNames(
              "rounded-lg transition",
              isCollapsed ? "p-2" : "p-2",
              {
                "bg-blue-500/10 text-blue-600 dark:text-blue-400":
                  openMenu === menuKey,
                "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-blue-500/10 group-hover:text-blue-500":
                  openMenu !== menuKey,
              },
            )}
          >
            {icon}
          </div>
          {!isCollapsed && <span className="font-medium">{title}</span>}
        </div>
        {!isCollapsed && (
          <FaChevronDown
            className={classNames(
              "transition-transform duration-200 text-gray-400",
              {
                "rotate-180": openMenu === menuKey,
              },
            )}
          />
        )}
      </button>

      <AnimatePresence>
        {openMenu === menuKey && !isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="ml-4 pl-6 mt-2 flex flex-col gap-1 overflow-hidden border-l border-gray-200 dark:border-gray-700"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Sidebar;
